import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Seo } from '@/seo/Seo'
import { useStorefrontContent, useBranding } from '@/branding/BrandingProvider'

// Role/route/icon are fixed (they map to registration flows + Keycloak roles);
// the visible label + description are brand-configurable via content.auth so a
// consumer brand can relabel Patient/Clinician/Caregiver → User/Coach/Family.
const accountTypeBase = [
  { role: 'patient', icon: 'solar:user-heart-linear', to: '/sign-up-patient' },
  { role: 'doctor', icon: 'solar:stethoscope-linear', to: '/sign-up-doctor' },
  { role: 'caregiver', icon: 'solar:hand-heart-linear', to: '/sign-up-caregiver' },
] as const

export function AccountTypePage() {
  const { auth } = useStorefrontContent()
  const { siteName } = useBranding()
  const roles = auth.accountType.roles as Record<string, { title: string; description: string }>
  const accountTypes = accountTypeBase.map((t) => ({ ...t, ...roles[t.role] }))
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 py-12">
      <Seo
        title={`Create Your ${siteName} Account`}
        description={`Get started with ${siteName}. ${auth.accountType.subtitle}`}
        path="/account-type"
      />
      <div className="w-full max-w-2xl">
        <Link
          to="/sign-in"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-8 transition-colors"
        >
          <Icon icon="solar:arrow-left-linear" /> Back to Sign In
        </Link>

        <h1 className="text-3xl font-medium tracking-tight text-navy mb-2">{auth.accountType.title}</h1>
        <p className="text-slate-500 font-light mb-10">{auth.accountType.subtitle}</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {accountTypes.map((type) => (
            <Link
              key={type.role}
              to={type.to}
              className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-navy/30 hover:shadow-lg transition-all group text-center"
            >
              <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-navy mb-4 mx-auto group-hover:scale-110 transition-transform">
                <Icon icon={type.icon} className="text-2xl" />
              </div>
              <h3 className="text-lg font-medium text-navy mb-2">{type.title}</h3>
              <p className="text-sm text-slate-500 font-light">{type.description}</p>
            </Link>
          ))}
        </div>

        <div className="mt-8 text-center">
          <span className="text-sm text-slate-500 font-light">Already have an account? </span>
          <Link to="/sign-in" className="text-sm font-medium text-rust hover:text-navy transition-colors">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  )
}
